import '../controls/neb-tab-group';

import { css, html } from 'lit';

import {
  setEnterpriseTenantPermissions,
  setEnterpriseAdmin,
  getEnterpriseAdmin,
} from '../../../../neb-api-client/src/enterprise-tenant-permissions-api-client';
import { store } from '../../../../neb-redux/neb-redux-store';
import {
  getTabWithError,
  map,
  arrayToObject,
} from '../../../../neb-utils/utils';
import NebIdentifier from '../field-groups/neb-identifier';
import NebProfile from '../field-groups/neb-profile';
import NebEnterpriseAdmin from '../permissions/neb-enterprise-admin';
import NebPermissions from '../permissions/neb-permissions';
import NebSecurity from '../security/neb-security';

import Form, { ELEMENTS as BASE_ELEMENTS } from './neb-form';
import {
  handleEnterpriseAdminSaveConfirmation,
  addEnterprisePermissionsTab,
  shouldDisplaySecurityTab,
  getSecurityTabConfig,
  initializeOwl2faFeatureFlag,
  initializeEnterpriseReportsFeatureFlag,
  handleCurrentUserEnterpriseAdminDisabled,
  getSessionState,
} from './utils/neb-form-user-util';

export const SPECIALIST_INFO = 'Specialist Professional Information';
export const ELEMENTS = {
  ...BASE_ELEMENTS,
  tabGroup: {
    id: 'tab-group',
  },
  tabItems: {
    selector: 'neb-tab-old',
  },
  permissions: {
    id: 'permissions',
  },
  profile: {
    id: 'profile',
  },
  identifiers: {
    id: 'identifiers',
  },
  security: {
    id: 'security',
  },
  enterpriseAdmin: {
    id: 'enterprise-admin',
  },
};
export default class NebUserSpecialistForm extends Form {
  static get properties() {
    return {
      __selectedTab: Boolean,
      __hasOwl2faEnablementFF: Boolean,
      __hasEnterpriseReportsFF: Boolean,
      payerItems: Array,
      providerItems: Array,
      activeLocations: Array,
      locations: Array,
      isSupportApp: { type: Boolean, reflect: true },
    };
  }

  static createModel() {
    return {
      ...NebProfile.createModel(),
      ...NebIdentifier.createModel(),
      ...NebPermissions.createModel(),
      ...NebEnterpriseAdmin.createModel(),
      ...NebSecurity.createModel(),
    };
  }

  createSelectors() {
    return {
      children: {
        ...NebProfile.createSelectors({ locations: this.activeLocations }),
        ...NebIdentifier.createSelectors({
          providers: this.providerItems,
          payers: this.payerItems,
        }),
        ...NebPermissions.createSelectors(this.locations),
        ...NebEnterpriseAdmin.createSelectors(),
        ...NebSecurity.createSelectors(),
      },
    };
  }

  __getBaseNavItems() {
    return [
      {
        id: 'profile',
        label: 'Profile',
        renderer: () => this.__renderProfile(),
        fields: ['name', 'phones', 'email', 'ddRetention'],
      },
      {
        id: 'identifiers',
        label: 'Specialist Identifiers',
        renderer: () => this.__renderIdentifier(),
        fields: [
          'taxId',
          'licenses',
          'identifiers',
          'taxonomy',
          'substituteProviders',
        ],
      },
      {
        id: 'permissions',
        label: 'Permissions & Rights',
        renderer: () => this.__renderPermissionsAndRights(),
        fields: ['superUser', 'permissions', 'locations'],
      },
    ];
  }

  initState() {
    super.initState();

    this.__selectedTab = 'profile';
    this.__hasOwl2faEnablementFF = false;
    this.__hasEnterpriseReportsFF = false;
    this.confirmLabel = 'Save Specialist';
    this.locations = [];
    this.activeLocations = [];
    this.payerItems = [];
    this.providerItems = [];
    this.isSupportApp = false;

    this.__navItems = this.__getBaseNavItems();
  }

  initHandlers() {
    super.initHandlers();
    this.handlers = {
      ...this.handlers,
      selectTab: tab => {
        this.__selectedTab = tab;
        this.requestUpdate();
      },
      save: async (formModel, options = {}) => {
        if (this.__selectedTab === 'security' || this.formService.validate()) {
          const rawModel = this.formService.build();
          const updatedModel = map(rawModel, (_, value) =>
            typeof value === 'string' ? value.trim() : value,
          );

          let originalEnterpriseAdmin = false;

          // Only fetch original admin status for existing users (not new user creation)
          if (updatedModel.id) {
            try {
              const state = store.getState();
              const currentTenantId = state?.session?.item?.tenantId;

              const originalResponse = await getEnterpriseAdmin(
                currentTenantId,
                updatedModel.id,
              );
              originalEnterpriseAdmin = !!originalResponse?.enterpriseAdmin;
            } catch (error) {
              originalEnterpriseAdmin = false;
            }
          }

          const actualOriginalModel = {
            ...this.model,
            enterpriseAdmin: originalEnterpriseAdmin,
          };

          const shouldProceed = await handleEnterpriseAdminSaveConfirmation({
            originalModel: actualOriginalModel,
            updatedModel,
            formService: this.formService,
            onChangeDirty: this.onChangeDirty,
          });

          if (!shouldProceed) {
            return;
          }

          this.__saving = true;

          const { enterpriseTenantPermissions, ...mainModel } = updatedModel;

          const modelWithFlags = {
            ...mainModel,
            skipDismiss: this.__selectedTab === 'security',
          };

          await this.onSave(modelWithFlags, options);

          await this.__saveEnterpriseSettings(
            updatedModel,
            enterpriseTenantPermissions,
            originalEnterpriseAdmin,
          );

          this.__saving = false;
        } else {
          this.__selectedTab = getTabWithError(
            arrayToObject(this.__navItems),
            this.errors,
            this.__selectedTab,
          );
        }
      },
    };
  }

  async connectedCallback() {
    super.connectedCallback();

    if (!this.isSupportApp) {
      this.__hasOwl2faEnablementFF = await initializeOwl2faFeatureFlag();
      this.__hasEnterpriseReportsFF =
        await initializeEnterpriseReportsFeatureFlag();
    }

    this.__navItems = await addEnterprisePermissionsTab({
      navItems: this.__navItems,
      renderEnterpriseAdmin: () => this.__renderEnterpriseAdmin(),
      isSupportApp: this.isSupportApp,
      model: this.model,
      hasEnterpriseReportsFF: this.__hasEnterpriseReportsFF,
      formType: 'Specialist',
    });

    if (shouldDisplaySecurityTab(this.model, this.__hasOwl2faEnablementFF)) {
      this.__navItems = [
        ...this.__navItems,
        getSecurityTabConfig(() => this.__renderSecurity()),
      ];
    }

    this.requestUpdate();
  }

  async __saveEnterpriseAdmin(currentTenantId, updatedModel) {
    if (updatedModel.id && updatedModel.enterpriseAdmin !== undefined) {
      await setEnterpriseAdmin(
        currentTenantId,
        updatedModel.id,
        updatedModel.enterpriseAdmin,
      );
    }
  }

  async __saveEnterpriseTenantPermissions(
    currentTenantId,
    updatedModel,
    enterpriseTenantPermissions,
  ) {
    if (
      updatedModel.id &&
      updatedModel.enterpriseId &&
      Array.isArray(enterpriseTenantPermissions)
    ) {
      await setEnterpriseTenantPermissions(
        currentTenantId,
        updatedModel.id,
        enterpriseTenantPermissions,
      );
    }
  }

  async __updateNavigationAfterDisabling(currentUserId) {
    if (this.model?.id === currentUserId) {
      this.__navItems = this.__getBaseNavItems();

      this.__navItems = await addEnterprisePermissionsTab({
        navItems: this.__navItems,
        renderEnterpriseAdmin: () => this.__renderEnterpriseAdmin(),
        isSupportApp: this.isSupportApp,
        model: this.model,
        hasEnterpriseReportsFF: this.__hasEnterpriseReportsFF,
        formType: 'Specialist',
      });

      if (shouldDisplaySecurityTab(this.model, this.__hasOwl2faEnablementFF)) {
        this.__navItems = [
          ...this.__navItems,
          getSecurityTabConfig(() => this.__renderSecurity()),
        ];
      }

      if (this.__selectedTab === 'enterpriseAdmin') {
        this.__selectedTab = 'profile';
      }

      this.requestUpdate();
      this.onCancel();
    }
  }

  async __saveEnterpriseSettings(
    updatedModel,
    enterpriseTenantPermissions,
    originalEnterpriseAdmin,
  ) {
    const { currentUserId } = getSessionState();
    const isCurrentUser = updatedModel.id === currentUserId;
    const isDisablingOwnAdmin =
      isCurrentUser &&
      originalEnterpriseAdmin &&
      updatedModel.enterpriseAdmin === false;

    const state = store.getState();
    const currentTenantId = state?.session?.item?.tenantId;

    await this.__saveEnterpriseAdmin(currentTenantId, updatedModel);
    await this.__saveEnterpriseTenantPermissions(
      currentTenantId,
      updatedModel,
      enterpriseTenantPermissions,
    );

    if (isDisablingOwnAdmin) {
      await handleCurrentUserEnterpriseAdminDisabled();
      await this.__updateNavigationAfterDisabling(currentUserId);
    }
  }

  static get styles() {
    return [
      super.styles,
      css`
        .layout {
          gap: 0;
          overflow-y: hidden;
          grid-template-rows: 45px auto;
        }

        .form {
          padding: 0;
        }

        .tabs {
          flex: 0 0 auto;
          padding-top: 12px;
        }

        .tab-content {
          flex: 1 0 0;
          overflow: auto;
        }
      `,
    ];
  }

  __renderProfile() {
    return html`
      <neb-profile
        id="${ELEMENTS.profile.id}"
        class="tab-content"
        name="profile"
        .model="${this.state}"
        .locations="${this.activeLocations}"
        .onSave="${this.handlers.save}"
        .onChange="${this.handlers.change}"
        .onAdd="${this.handlers.addItem}"
        .onRemove="${this.handlers.removeItem}"
        .onCancel="${this.handlers.cancel}"
        .errors="${this.errors}"
        .disableEmail="${this.model.email}"
        .layout="${this.layout}"
        show-biography
      ></neb-profile>
    `;
  }

  __renderIdentifier() {
    return html`
      <neb-identifier
        id="${ELEMENTS.identifiers.id}"
        class="tab-content"
        .model="${this.state}"
        .onChange="${this.handlers.change}"
        .onAdd="${this.handlers.addItem}"
        .onRemove="${this.handlers.removeItem}"
        .errors="${this.errors}"
        .header="${SPECIALIST_INFO}"
        .providerItems="${this.providerItems}"
        .payerItems="${this.payerItems}"
        .layout="${this.layout}"
      ></neb-identifier>
    `;
  }

  __renderPermissionsAndRights() {
    return html`
      <neb-permissions
        id="${ELEMENTS.permissions.id}"
        class="tab-content"
        name="permissions"
        .layout="${this.layout}"
        .model="${this.state}"
        .errors="${this.errors}"
        .onChange="${this.handlers.change}"
        .locations="${this.locations}"
      ></neb-permissions>
    `;
  }

  __renderEnterpriseAdmin() {
    return html`
      <neb-enterprise-admin
        id="${ELEMENTS.enterpriseAdmin.id}"
        class="tab-content"
        .layout="${this.layout}"
        .model="${this.state}"
        .errors="${this.errors}"
        .onChange="${this.handlers.change}"
        .userId="${this.model?.id}"
      ></neb-enterprise-admin>
    `;
  }

  __renderSecurity() {
    return html`
      <neb-security
        id="${ELEMENTS.security.id}"
        class="tab-content"
        name="security"
        .layout="${this.layout}"
        .model="${this.state}"
        .errors="${this.errors}"
        .onChange="${this.handlers.change}"
        .onSave="${this.handlers.save}"
        .onChangeDirty="${this.onChangeDirty}"
      ></neb-security>
    `;
  }

  __renderTabContent() {
    const item = this.__navItems.find(i => i.id === this.__selectedTab);
    return item ? item.renderer() : '';
  }

  renderContent() {
    return html`
      <neb-tab-group
        class="tabs"
        id="${ELEMENTS.tabGroup.id}"
        .selectedId="${this.__selectedTab}"
        .items="${this.__navItems}"
        .onSelect="${this.handlers.selectTab}"
        .layout="${this.layout}"
      ></neb-tab-group>
      ${this.__renderTabContent()}
    `;
  }
}

customElements.define('neb-form-user-specialist', NebUserSpecialistForm);
